export type SocialEmbedProvider = "youtube" | "loom" | "instagram" | "tiktok";

export type NormalizedSocialEmbed = {
  provider: SocialEmbedProvider;
  normalizedUrl: string;
  sourceUrl: string;
};

const ensureAbsoluteUrl = (value: unknown) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return new URL(trimmed);
  } catch {
    try {
      return new URL(`https://${trimmed}`);
    } catch {
      return null;
    }
  }
};

const normalizeYouTubeEmbed = (url: URL) => {
  const hostname = url.hostname.toLowerCase();
  const pathname = url.pathname;
  let videoId = "";

  if (hostname === "youtu.be") {
    videoId = pathname.split("/").filter(Boolean)[0] ?? "";
  } else if (hostname.endsWith("youtube.com") || hostname.endsWith("youtube-nocookie.com")) {
    if (pathname === "/watch") {
      videoId = url.searchParams.get("v") ?? "";
    } else if (pathname.startsWith("/shorts/")) {
      videoId = pathname.split("/")[2] ?? "";
    } else if (pathname.startsWith("/embed/")) {
      videoId = pathname.split("/")[2] ?? "";
    } else if (pathname.startsWith("/live/")) {
      videoId = pathname.split("/")[2] ?? "";
    }
  }

  if (!videoId) {
    return null;
  }

  return {
    provider: "youtube" as const,
    normalizedUrl: `https://www.youtube.com/embed/${videoId}`,
  };
};

const normalizeInstagramEmbed = (url: URL) => {
  const hostname = url.hostname.toLowerCase();
  if (!hostname.endsWith("instagram.com")) {
    return null;
  }

  const match = url.pathname.match(/^\/(p|reel|reels|tv)\/([^/?#]+)/i);
  if (!match) {
    return null;
  }

  const contentType = match[1].toLowerCase() === "reels" ? "reel" : match[1].toLowerCase();
  const contentId = match[2];

  return {
    provider: "instagram" as const,
    normalizedUrl: `https://www.instagram.com/${contentType}/${contentId}/embed`,
  };
};

const normalizeTikTokEmbed = (url: URL) => {
  const hostname = url.hostname.toLowerCase();
  if (!hostname.endsWith("tiktok.com")) {
    return null;
  }

  let videoId = "";

  const embedMatch = url.pathname.match(/^\/embed\/v2\/([0-9]+)/i);
  if (embedMatch) {
    videoId = embedMatch[1] ?? "";
  }

  if (!videoId) {
    const videoMatch = url.pathname.match(/^\/@[^/]+\/video\/([0-9]+)/i);
    if (videoMatch) {
      videoId = videoMatch[1] ?? "";
    }
  }

  if (!videoId) {
    return null;
  }

  return {
    provider: "tiktok" as const,
    normalizedUrl: `https://www.tiktok.com/embed/v2/${videoId}`,
  };
};

const normalizeLoomEmbed = (url: URL) => {
  const hostname = url.hostname.toLowerCase();
  if (!hostname.endsWith("loom.com")) {
    return null;
  }

  const parts = url.pathname.split("/").filter(Boolean);
  const videoId =
    parts[0] === "share" || parts[0] === "embed"
      ? parts[1] ?? ""
      : parts[0] === "looms" && parts[1] === "share"
        ? parts[2] ?? ""
        : "";

  if (!videoId) {
    return null;
  }

  return {
    provider: "loom" as const,
    normalizedUrl: `https://www.loom.com/embed/${videoId}`,
  };
};

export const normalizeSocialEmbedUrl = (
  value: unknown,
): NormalizedSocialEmbed | null => {
  const parsedUrl = ensureAbsoluteUrl(value);
  if (!parsedUrl) {
    return null;
  }

  const protocol = parsedUrl.protocol.toLowerCase();
  if (protocol !== "https:" && protocol !== "http:") {
    return null;
  }

  const normalized =
    normalizeYouTubeEmbed(parsedUrl)
    ?? normalizeLoomEmbed(parsedUrl)
    ?? normalizeInstagramEmbed(parsedUrl)
    ?? normalizeTikTokEmbed(parsedUrl);

  if (!normalized) {
    return null;
  }

  return {
    ...normalized,
    sourceUrl: parsedUrl.toString(),
  };
};

export const isValidEmbeddableUrl = (value: unknown) => {
  const parsedUrl = ensureAbsoluteUrl(value);
  if (!parsedUrl) {
    return false;
  }

  const protocol = parsedUrl.protocol.toLowerCase();
  return protocol === "https:" || protocol === "http:";
};
